import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, CheckCircle, Clock, Truck, Star, Phone, ChevronDown } from "lucide-react";
import { useInView } from "../hooks/useInView";
import { services, industries } from "../data/services";
import { blogPosts } from "../data/blogPosts";
import QuoteForm from "../components/QuoteForm";
import { useState } from "react";

const trustItems = [
  { icon: Clock, text: "7-Day Turnaround" },
  { icon: Truck, text: "Free DFW Delivery" },
  { icon: Star, text: "500+ 5-Star Reviews" },
  { icon: CheckCircle, text: "No Minimum DTF" },
  { icon: CheckCircle, text: "Free Art Review" },
  { icon: Clock, text: "Rush Orders Available" },
];

const testimonials = [
  { name: "Marcus T.", company: "Dallas Marketing Agency", stars: 5, text: "Ninja Print delivered 300 shirts in 5 days for our corporate retreat. Colors were exact Pantone matches and quality was outstanding. Our team was blown away." },
  { name: "Coach Rodriguez", company: "Dallas YMCA Soccer", stars: 5, text: "Best jersey experience we've ever had. They handled our entire roster list, added individual names and numbers, and delivered on time. The team loves the jerseys." },
  { name: "Sarah M.", company: "Oak Cliff Restaurant Group", stars: 5, text: "We needed staff uniforms for 3 locations in 2 weeks. Ninja Print made it happen with embroidered polos that look incredibly professional. We're customers for life." },
  { name: "James K.", company: "Frisco Tech Startup", stars: 5, text: "Used DTF for a small run of 15 shirts for our product launch party. No minimum, super fast, and the print quality was just like our mockup. Highly recommend." },
  { name: "Pastor Williams", company: "Dallas Community Church", stars: 5, text: "Mission trip shirts for 80 volunteers, delivered to our church. The design team helped us create something meaningful and the print quality was exceptional." },
];

const faqs = [
  { q: "What is the minimum order for screen printing in Dallas?", a: "Our minimum for screen printing is 12 pieces. For smaller quantities, we offer DTF printing with absolutely no minimum — you can order even 1 shirt." },
  { q: "How fast can I get custom shirts in Dallas?", a: "Standard turnaround is 7-10 business days from art approval. We offer rush options: 5-7 day (+$2/pc), 3-4 day (+$4/pc), and same-day for select orders. Call us for urgent needs." },
  { q: "Do you offer free delivery in DFW?", a: "Yes! All orders of 24+ pieces receive free local delivery anywhere in the DFW Metroplex. We deliver to your business, home, event venue, or wherever you need it." },
  { q: "Can I see a proof before my shirts are printed?", a: "Absolutely. We provide a digital proof/mockup for approval before any shirt goes on the press. No production begins until you've approved the art." },
  { q: "What's the difference between screen printing and DTF?", a: "Screen printing uses physical screens and is most cost-effective for 24+ pieces. DTF (Direct to Film) has no minimums, handles unlimited colors, and works on any fabric — ideal for small batches and complex designs." },
  { q: "Do you offer Pantone color matching?", a: "Yes, we offer precise Pantone PMS color matching for all screen print orders. Include your Pantone codes with your order and we'll match them exactly." },
  { q: "Can you print on garments I already own?", a: "Yes! We can print on customer-supplied garments. Bring your blanks to us or ship them to our Dallas facility. Note: we're not responsible for damage to customer-supplied garments, and pricing may vary." },
  { q: "Do you offer graphic design services?", a: "Yes! Our in-house Dallas design team creates logos, custom apparel graphics, and print-ready artwork starting at $75. All files are yours to keep. Rush 24-hour design available." },
];

function StatCounter({ value, label }) {
  return (
    <div className="text-center">
      <div className="font-teko text-5xl lg:text-6xl text-red-500 leading-none">{value}</div>
      <div className="text-xs text-neutral-400 uppercase tracking-widest font-inter mt-1">{label}</div>
    </div>
  );
}

function FAQItem({ faq, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-neutral-800 hover:border-neutral-700 transition-colors">
      <button
        data-testid={`faq-item-${index}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between p-5 text-left gap-4"
      >
        <span className="font-teko text-lg text-white uppercase tracking-wide leading-tight">{faq.q}</span>
        <ChevronDown size={18} className={`text-red-500 flex-shrink-0 mt-0.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-neutral-400 font-inter leading-relaxed">
          {faq.a}
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const [heroRef, heroVisible] = useInView({ threshold: 0.1 });
  const [servicesRef, servicesVisible] = useInView({ threshold: 0.05 });
  const [statsRef, statsVisible] = useInView({ threshold: 0.2 });

  return (
    <>
      <Helmet>
        <title>Ninja Screen Print | #1 Custom Apparel Printing in Dallas TX | DFW Screen Printing</title>
        <meta name="description" content="Dallas's premier screen printing company. Custom t-shirts, DTF printing, embroidery & specialty printing. Fast 7-day turnaround. Free DFW delivery. Get a free quote in 2 hours." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question", "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ paddingTop: "64px" }}
      >
        {/* Background Video */}
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          poster="https://images.unsplash.com/photo-1581092160607-ee22731c9e11?w=1600&q=80"
        >
          <source src="https://customer-assets.emergentagent.com/job_419da4a4-c2d8-43a0-81d2-8591121c3d6f/artifacts/ykdt1rwm_Video_Generation_Heat_Transfer_Sublimation.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-[#0A0A0A]" />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div ref={heroRef} className={`transition-all duration-700 ${heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-px bg-red-600" />
                <span className="text-red-500 text-xs uppercase tracking-[0.3em] font-inter">Dallas, TX — DFW's #1 Screen Printer</span>
              </div>
              <h1 className="font-teko text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-white uppercase leading-tight tracking-tight">
                MASTERY IN INK.<br />
                <span className="text-red-500">PRECISION</span> IN PRINT.
              </h1>
              <p className="font-inter text-base text-neutral-300 mt-6 max-w-lg leading-relaxed">
                Dallas's premier screen printing company. Custom shirts, DTF printing, embroidery & specialty printing — delivered free to your door anywhere in DFW. Fast turnaround. No compromises.
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link data-testid="hero-quote-btn" to="/contact" className="ninja-btn-primary text-base font-teko tracking-widest px-8 py-4">
                  GET A FREE QUOTE
                </Link>
                <a href="tel:+12145550190" data-testid="hero-call-btn" className="ninja-btn-secondary text-sm font-inter px-8 py-4 flex items-center gap-2">
                  <Phone size={14} /> (214) 555-0190
                </a>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8">
                {["Free 2-Hour Quote", "No Minimum DTF", "Free DFW Delivery", "7-Day Rush Available"].map((item) => (
                  <div key={item} className="flex items-center gap-1.5 text-sm text-neutral-400 font-inter">
                    <CheckCircle size={12} className="text-red-500" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
            {/* Hero Form */}
            <div className="bg-[#0f0f0f]/90 border border-neutral-800 backdrop-blur-sm p-6 sm:p-8">
              <div className="mb-4">
                <div className="w-6 h-px bg-red-600 mb-2" />
                <h2 className="font-teko text-2xl text-white uppercase tracking-wide">Quick Quote Request</h2>
                <p className="text-xs text-neutral-500 font-inter">Response within 2 hours. No obligation.</p>
              </div>
              <QuoteForm compact />
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <div className="bg-red-700 py-3 overflow-hidden" data-testid="trust-bar">
        <div className="animate-marquee trust-bar-inner">
          {[...trustItems, ...trustItems].map((item, i) => (
            <div key={i} className="flex items-center gap-2 px-8 shrink-0">
              <item.icon size={14} className="text-white/80" />
              <span className="text-white text-xs uppercase tracking-widest font-inter whitespace-nowrap">{item.text}</span>
              <span className="text-red-400 ml-8">|</span>
            </div>
          ))}
        </div>
      </div>

      {/* SERVICES BENTO GRID */}
      <section data-testid="services-section" className="ninja-section" ref={servicesRef}>
        <div className="ninja-container">
          <div className="mb-12">
            <div className="red-line" />
            <h2 className="font-teko text-4xl sm:text-5xl text-white uppercase tracking-tight">
              Our Services
            </h2>
            <p className="text-neutral-400 font-inter mt-2 max-w-xl">Full-service custom apparel printing for DFW businesses, schools, sports teams, and events.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service, i) => (
              <Link
                key={service.slug}
                to={`/services/${service.slug}`}
                data-testid={`service-card-${service.slug}`}
                className={`service-card ninja-card relative overflow-hidden p-7 group hover:-translate-y-1 transition-all duration-300 ${i === 0 ? "lg:col-span-2 lg:row-span-1" : ""}`}
              >
                <div className="absolute top-0 left-0 w-1 h-full bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="text-xs text-red-500 uppercase tracking-widest font-inter mb-1">Starting at {service.startingAt}/pc</div>
                    <h3 className="font-teko text-2xl text-white uppercase tracking-wide">{service.title}</h3>
                    <p className="text-xs text-neutral-500 font-inter">{service.subtitle}</p>
                  </div>
                  <ArrowRight size={16} className="text-red-500 opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
                </div>
                <p className="text-neutral-400 font-inter text-sm leading-relaxed">{service.description}</p>
                <div className="mt-4 flex flex-wrap gap-1">
                  {service.bestFor.slice(0, 3).map((tag) => (
                    <span key={tag} className="text-[10px] text-neutral-500 border border-neutral-800 px-2 py-0.5 uppercase tracking-wider font-inter">{tag}</span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/services" data-testid="all-services-btn" className="ninja-btn-secondary text-sm font-teko tracking-widest px-10 py-3">
              VIEW ALL SERVICES
            </Link>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-16 bg-[#0d0d0d] border-y border-neutral-800" ref={statsRef}>
        <div className="ninja-container">
          <div className={`grid grid-cols-2 lg:grid-cols-4 gap-8 transition-all duration-700 ${statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <StatCounter value="500+" label="5-Star Reviews" />
            <StatCounter value="15K+" label="Orders Completed" />
            <StatCounter value="7" label="Day Standard Turnaround" />
            <StatCounter value="16" label="DFW Cities Served" />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="ninja-section" data-testid="how-it-works">
        <div className="ninja-container">
          <div className="text-center mb-12">
            <div className="w-8 h-px bg-red-600 mx-auto mb-4" />
            <h2 className="font-teko text-4xl sm:text-5xl text-white uppercase tracking-tight">How It Works</h2>
            <p className="text-neutral-400 font-inter mt-2">From quote to delivery — simplified into 4 steps</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Submit Quote Request", desc: "Fill out our order brief with your details, quantity, and design status. We respond within 2 hours with exact pricing." },
              { step: "02", title: "Approve Your Proof", desc: "Our art team prepares a digital mockup of your design on your chosen garment. Nothing prints until you approve." },
              { step: "03", title: "We Print Your Order", desc: "Your job goes into production on our schedule with the rush level you chose. Quality control at every step." },
              { step: "04", title: "Free DFW Delivery", desc: "Your finished apparel is delivered free anywhere in DFW on orders of 24+ pieces. Right to your door." },
            ].map((item) => (
              <div key={item.step} data-testid={`step-${item.step}`} className="relative">
                <div className="font-teko text-8xl text-neutral-900 leading-none select-none absolute -top-4 -left-2">{item.step}</div>
                <div className="relative ninja-card p-6">
                  <div className="text-xs text-red-500 uppercase tracking-widest font-teko text-2xl mb-2">{item.step}</div>
                  <h3 className="font-teko text-xl text-white uppercase tracking-wide mb-2">{item.title}</h3>
                  <p className="text-neutral-400 font-inter text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="ninja-section bg-[#0d0d0d]" data-testid="industries-section">
        <div className="ninja-container">
          <div className="mb-12">
            <div className="red-line" />
            <h2 className="font-teko text-4xl sm:text-5xl text-white uppercase tracking-tight">Industries We Serve</h2>
            <p className="text-neutral-400 font-inter mt-2 max-w-xl">Custom apparel for every sector of the Dallas economy.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {industries.map((ind) => (
              <Link
                key={ind.slug}
                to="/services"
                data-testid={`industry-${ind.slug}`}
                className="ninja-card p-5 group hover:-translate-y-1 transition-all duration-300"
              >
                <div className="text-xs text-red-500 uppercase tracking-widest font-inter mb-3 group-hover:text-red-400 transition-colors">{ind.services[0]}</div>
                <h3 className="font-teko text-lg text-white uppercase tracking-wide leading-tight mb-2">{ind.title}</h3>
                <p className="text-neutral-500 font-inter text-xs leading-relaxed">{ind.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="ninja-section" data-testid="testimonials-section">
        <div className="ninja-container">
          <div className="text-center mb-12">
            <div className="w-8 h-px bg-red-600 mx-auto mb-4" />
            <h2 className="font-teko text-4xl sm:text-5xl text-white uppercase tracking-tight">What Dallas Says</h2>
            <p className="text-neutral-400 font-inter mt-2">500+ 5-star reviews from businesses, schools, and organizations across DFW</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {testimonials.slice(0, 3).map((t, i) => (
              <div key={i} data-testid={`testimonial-${i}`} className="ninja-card p-6">
                <div className="flex gap-0.5 mb-3">
                  {[...Array(t.stars)].map((_, s) => (
                    <Star key={s} size={12} className="text-red-500 fill-red-500" />
                  ))}
                </div>
                <p className="text-neutral-300 font-inter text-sm leading-relaxed mb-4 italic">"{t.text}"</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-red-600/20 border border-red-600/30 flex items-center justify-center text-red-500 font-teko text-lg">
                    {t.name[0]}
                  </div>
                  <div>
                    <div className="text-white text-sm font-medium font-inter">{t.name}</div>
                    <div className="text-neutral-500 text-xs font-inter">{t.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section className="ninja-section bg-[#0d0d0d]" data-testid="pricing-preview">
        <div className="ninja-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="red-line" />
              <h2 className="font-teko text-4xl sm:text-5xl text-white uppercase tracking-tight">
                Transparent Pricing. No Surprises.
              </h2>
              <p className="text-neutral-400 font-inter mt-4 leading-relaxed">
                Screen printing pricing from $5.00/piece at volume. The more you order, the more you save. No hidden fees, no setup surprises for orders of 24+.
              </p>
              <div className="mt-6 space-y-3">
                {[
                  { qty: "24-47 pieces", price: "$11.00/pc", note: "Perfect for events & promos" },
                  { qty: "48-71 pieces", price: "$8.50/pc", note: "Sports teams & corporate" },
                  { qty: "144-287 pieces", price: "$6.00/pc", note: "Large organizations" },
                  { qty: "288+ pieces", price: "$5.00/pc", note: "Wholesale pricing" },
                ].map((item) => (
                  <div key={item.qty} className="flex items-center justify-between border-b border-neutral-800 pb-3">
                    <div>
                      <span className="font-teko text-lg text-white uppercase">{item.qty}</span>
                      <span className="text-neutral-500 font-inter text-xs ml-2">— {item.note}</span>
                    </div>
                    <span className="font-teko text-xl text-red-500">{item.price}</span>
                  </div>
                ))}
              </div>
              <p className="text-neutral-600 text-xs font-inter mt-3">* Printing only. Blank garments additional. 1-color design pricing shown.</p>
              <Link to="/pricing" data-testid="view-pricing-btn" className="ninja-btn-primary mt-6 inline-block font-teko tracking-widest">
                VIEW FULL PRICING GUIDE
              </Link>
            </div>
            <div className="ninja-card p-8">
              <div className="red-line" />
              <h3 className="font-teko text-2xl text-white uppercase tracking-wide mb-6">Get Exact Pricing for Your Order</h3>
              <QuoteForm compact />
            </div>
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="ninja-section" data-testid="blog-section">
        <div className="ninja-container">
          <div className="flex justify-between items-end mb-12">
            <div>
              <div className="red-line" />
              <h2 className="font-teko text-4xl sm:text-5xl text-white uppercase tracking-tight">Screen Printing Insights</h2>
              <p className="text-neutral-400 font-inter mt-2">Expert guides, pricing tips, and Dallas market knowledge</p>
            </div>
            <Link to="/blog" data-testid="all-posts-btn" className="hidden sm:flex items-center gap-2 text-sm text-red-500 hover:text-red-400 transition-colors font-inter uppercase tracking-widest">
              All Posts <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogPosts.slice(0, 3).map((post, i) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                data-testid={`blog-card-${i}`}
                className="ninja-card group overflow-hidden"
              >
                <div className="blog-img-wrap h-44 overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-red-500 uppercase tracking-widest font-inter">{post.category}</span>
                    <span className="text-neutral-700">•</span>
                    <span className="text-xs text-neutral-600 font-inter">{post.readTime}</span>
                  </div>
                  <h3 className="font-teko text-xl text-white uppercase tracking-wide leading-tight group-hover:text-red-400 transition-colors mb-2">{post.title}</h3>
                  <p className="text-neutral-500 font-inter text-xs leading-relaxed line-clamp-2">{post.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="ninja-section bg-[#0d0d0d]" data-testid="faq-section">
        <div className="ninja-container">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <div className="red-line" />
              <h2 className="font-teko text-4xl sm:text-5xl text-white uppercase tracking-tight">
                Frequently Asked Questions
              </h2>
              <p className="text-neutral-400 font-inter mt-4 leading-relaxed">
                Common questions about custom screen printing, DTF, and embroidery in Dallas. Don't see your question? Call us — (214) 555-0190.
              </p>
              <div className="mt-8 p-6 bg-red-900/20 border border-red-900/40">
                <p className="font-teko text-2xl text-white uppercase">Still Have Questions?</p>
                <p className="text-neutral-400 font-inter text-sm mt-1 mb-4">Our Dallas team responds within 2 hours to every quote request.</p>
                <Link to="/contact" data-testid="faq-cta-btn" className="ninja-btn-primary font-teko tracking-widest text-sm px-6 py-3">
                  GET A FREE QUOTE
                </Link>
              </div>
            </div>
            <div className="space-y-2">
              {faqs.map((faq, i) => (
                <FAQItem key={i} faq={faq} index={i} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        className="py-28 relative overflow-hidden"
        data-testid="final-cta"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1400&q=80')", backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-[#0A0A0A]/88" />
        <div className="relative z-10 ninja-container text-center">
          <div className="w-8 h-px bg-red-600 mx-auto mb-4" />
          <h2 className="font-teko text-5xl sm:text-6xl lg:text-7xl text-white uppercase tracking-tight">
            Ready to Order Custom <span className="text-red-500">Dallas</span> Apparel?
          </h2>
          <p className="text-neutral-300 font-inter mt-4 max-w-xl mx-auto">
            Free quote in 2 hours. Free DFW delivery. Rush orders available. Let's create something legendary together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link to="/contact" data-testid="final-cta-btn" className="ninja-btn-primary font-teko tracking-widest text-xl px-12 py-5">
              GET YOUR FREE QUOTE
            </Link>
            <Link to="/pricing" className="ninja-btn-secondary font-teko tracking-widest text-xl px-12 py-5">
              VIEW PRICING
            </Link>
          </div>
          <p className="text-neutral-500 font-inter text-xs mt-6">Serving Dallas · Fort Worth · Plano · Irving · Frisco · Arlington · and all of DFW</p>
        </div>
      </section>
    </>
  );
}
